// SidebarLayout.tsx
// Extension shell — header with Auto Fill + Web View buttons, content area below.

import { ReactNode, useCallback, useMemo, useState } from "react";
import { Send, Lock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import appIcon from "@/assets/app-icon.png";
import { loadState } from "@/lib/storage";
import { loadUsage, getAccessTier, incrementActionCount } from "@/lib/usage";
import { APP_ORIGIN, WEBVIEW_URL } from "@/lib/siteUrls";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/analytics";

interface SidebarLayoutProps {
  children: ReactNode;
  showAutoFill?: boolean;
}

export default function SidebarLayout({ children, showAutoFill = false }: SidebarLayoutProps) {
  const { user, subscription } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  /** Bumped after a successful anonymous autofill so we re-read usage from localStorage. */
  const [usageBump, setUsageBump] = useState(0);

  const { usageTier, isAccessActive } = useMemo(() => {
    const usage = !user ? loadUsage() : null;
    const tier = usage ? getAccessTier(usage) : null;
    const active =
      subscription.subscribed || tier === "free" || tier === "nudge";
    return { usageTier: tier, isAccessActive: active };
  }, [user, subscription.subscribed, usageBump]);

  const handleAutoFill = useCallback(async () => {
    if (!isAccessActive) {
      toast.error("Subscribe to Jobily Pro to use Auto Fill");
      return;
    }

    setIsLoading(true);
    trackEvent("autofill_clicked");
    try {
      const state = loadState();
      const { personal, summary, commonAnswers } = state?.profile || {};

      if (!personal?.fullName || !personal?.email) {
        toast.error("Please complete your profile with at least name and email");
        setIsLoading(false);
        return;
      }

      const payload = {
        type: "FILL_FORM",
        data: {
          // Core
          fullName: personal.fullName || "",
          email: personal.email || "",
          phone: personal.phone || "",
          location: personal.location || "",
          // Address
          addressLine1: personal.addressLine1 || "",
          addressLine2: personal.addressLine2 || "",
          city: personal.city || "",
          state: personal.state || "",
          country: personal.country || "",
          postcode: personal.postcode || "",
          // Extended
          nationality: personal.nationality || "",
          dateOfBirth: personal.dateOfBirth || "",
          gender: personal.gender || "",
          workAuthorization: personal.workAuthorization || "",
          yearsOfExperience: personal.yearsOfExperience || "",
          seniorityLevel: personal.seniorityLevel || "",
          timeZone: personal.timeZone || "",
          linkedin: personal.linkedIn || "",
          portfolio: personal.portfolio || "",
          github: personal.github || "",
          summary: summary || "",
          // Common answers
          salary: commonAnswers?.salary || "",
          noticePeriod: commonAnswers?.noticePeriod || "",
        },
      };

      const chromeGlobal = (window as any).chrome;
      if (chromeGlobal?.runtime?.sendMessage) {
        chromeGlobal.runtime.sendMessage(payload, (response: any) => {
          if (chromeGlobal.runtime.lastError) {
            toast.error("Autofill failed: " + chromeGlobal.runtime.lastError.message);
          } else if (response?.status === "error") {
            toast.error("Autofill error: " + response.error);
          } else if (response?.status === "filled") {
            trackEvent("autofill_success", {
              field_count: Object.keys(payload.data).length,
            });
            toast.success("Form filled successfully!");
            if (!user) {
              incrementActionCount();
              setUsageBump((n) => n + 1);
            }
          } else {
            toast.info("Autofill sent, but no confirmation from page");
          }
          setIsLoading(false);
        });
      } else {
        console.log("Auto-fill payload:", payload);
        toast.info("Auto-fill only works in the extension");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Autofill error:", error);
      toast.error("Something went wrong");
      setIsLoading(false);
    }
  }, [isAccessActive, user]);

  const handleOpenWebView = async () => {
    trackEvent("webview_opened");
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    const refresh = data?.session?.refresh_token;
    if (token && refresh) {
      // Tokens in location.search so WebViewPage can read URLSearchParams(window.location.search)
      window.open(
        `${APP_ORIGIN}/?access_token=${encodeURIComponent(token)}&refresh_token=${encodeURIComponent(refresh)}#/webview`,
        "_blank"
      );
    } else {
      window.open(WEBVIEW_URL, "_blank");
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-muted/50 p-4">
      <div className="w-[400px] min-h-[700px] max-h-[800px] bg-card rounded-2xl shadow-elevated border-0 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-center gap-2 bg-card">
          <img src={appIcon} alt="Jobily" className="w-8 h-8 rounded-lg" />
          <h1 className="font-display font-bold text-lg text-foreground tracking-tight">Jobily</h1>
          {showAutoFill && (
            <div className="ml-auto flex items-center gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1 text-xs h-7 px-2.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
                onClick={handleOpenWebView}
                disabled={!user}
                title={!user ? "Sign in to open the Web Portal with your account" : undefined}
              >
                <ExternalLink className="w-3 h-3" />
                Web
              </Button>
              <Button
                size="sm"
                className={`gap-1.5 text-xs h-7 px-3 ${
                  isAccessActive
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : ""
                }`}
                variant={isAccessActive ? "default" : "outline"}
                onClick={handleAutoFill}
                disabled={!isAccessActive || isLoading}
              >
                {isAccessActive ? (
                  <Send className="w-3 h-3" />
                ) : (
                  <Lock className="w-3 h-3" />
                )}
                {isLoading ? "Filling..." : "Auto Fill"}
              </Button>
            </div>
          )}
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
          {children}
        </div>
      </div>
    </div>
  );
}
